import { addHours, addMinutes } from 'date-fns'

import type { Time } from '@/types/time'
import { formatTime } from '@/utils/format-date'

import type { TimetableSettings } from '../queries/timetableSettingsQueryOptions'
import { getDurationFromTime } from '../utils/duration'

type SpecialBreak = { type: 'specialBreak'; from: Time; to: Time }
type Lesson = { type: 'lesson'; from: Time; to: Time; ordinal: number }
export type Activity = Lesson | SpecialBreak
export type ActivityType = Activity['type']
type SchoolDay = Activity[]

/**
 * Converts a `Time` string to a `Date` instance set to January 1, 1970,
 * with the provided hours and minutes.
 *
 * @param {Time} time - The time string containing hours and minutes.
 * @returns {Date} A `Date` object representing the given time on 1970-01-01.
 */
const timeToDate = (time: Time): Date => {
  const { hours, minutes } = getDurationFromTime(time)
  return new Date(1970, 0, 1, hours, minutes)
}

/**
 * Converts a `Date` object to a `Time` string in `HH:mm` format.
 *
 * @param {Date} date - The date to convert.
 * @returns {Time} The formatted time string.
 */
const dateToTime = (date: Date): Time => formatTime(date) as Time

/**
 * Adds a time duration (in `Time` string format) to a given `Date` object.
 *
 * @param {Date} date - The starting date and time.
 * @param {Time} time - The time duration to add, in `HH:mm` format.
 * @returns {Date} A new `Date` object with the added duration.
 */
const addTime = (date: Date, time: Time): Date => {
  const { hours, minutes } = getDurationFromTime(time)
  return addMinutes(addHours(date, hours), minutes)
}

/**
 * Generates a list of activities (lessons and special breaks) representing a school day,
 * based on the provided timetable settings.
 *
 * Each lesson and special break is represented as an object with its type, start and end times,
 * and ordinal number for lessons. Special breaks are inserted after specified slots.
 *
 * @param {TimetableSettings} timetableSettings - The timetable configuration, including start hour, slot duration, break duration, number of slots, and custom breaks.
 * @returns {SchoolDay} An array of activities (lessons and special breaks) in chronological order for the school day.
 */
export const createSchoolDay = (
  timetableSettings: TimetableSettings
): SchoolDay => {
  const { startHour, slotDuration, breakDuration, numOfSlots, customBreaks } =
    timetableSettings

  const activities: SchoolDay = []
  let currentTime = timeToDate(startHour)

  for (let i = 0; i < numOfSlots; i++) {
    const lessonStart = currentTime
    const lessonEnd = addTime(lessonStart, slotDuration)
    activities.push({
      type: 'lesson',
      from: dateToTime(lessonStart),
      to: dateToTime(lessonEnd),
      ordinal: i + 1
    })
    currentTime = lessonEnd

    // Check for special break after this slot
    const specialBreak = customBreaks.find(customBreak =>
      customBreak.slots.includes(i)
    )
    if (specialBreak) {
      const specialBreakStart = currentTime
      const specialBreakEnd = addTime(specialBreakStart, specialBreak.duration)
      activities.push({
        type: 'specialBreak',
        from: dateToTime(specialBreakStart),
        to: dateToTime(specialBreakEnd)
      })
      currentTime = specialBreakEnd
    } else if (i !== numOfSlots) {
      // Add regular break if not the last slot
      currentTime = addTime(currentTime, breakDuration)
    }
  }

  return activities
}
